/**
 * Team chat store — session-only state for the internal messaging widget.
 * No persistence; everything resets on tab close.
 */

import { create } from 'zustand'
import type { TeamChannel, TeamMessage } from '@/api/team'

interface TeamState {
  // Panel
  panelOpen: boolean
  activeChannelId: string | null

  // Data
  channels: TeamChannel[]
  messages: Record<string, TeamMessage[]>
  onlineUsers: string[]
  typingUsers: Record<string, string[]>

  // Actions
  togglePanel: () => void
  openChannel: (id: string) => void
  goBack: () => void
  setChannels: (channels: TeamChannel[]) => void
  upsertChannel: (channel: TeamChannel) => void
  appendMessage: (msg: TeamMessage) => void
  prependMessages: (channelId: string, msgs: TeamMessage[]) => void
  setUserOnline: (userId: string, online: boolean) => void
  setTyping: (channelId: string, userId: string, isTyping: boolean) => void
  decrementUnread: (channelId: string) => void
}

export const useTeamStore = create<TeamState>()((set) => ({
  panelOpen: false,
  activeChannelId: null,

  channels: [],
  messages: {},
  onlineUsers: [],
  typingUsers: {},

  togglePanel: () => set((s) => ({ panelOpen: !s.panelOpen })),

  openChannel: (id) => set({ activeChannelId: id }),

  goBack: () => set({ activeChannelId: null }),

  setChannels: (channels) => set({ channels }),

  upsertChannel: (channel) =>
    set((s) => {
      const idx = s.channels.findIndex((c) => c.id === channel.id)
      if (idx >= 0) {
        const next = [...s.channels]
        next[idx] = channel
        return { channels: next }
      }
      return { channels: [channel, ...s.channels] }
    }),

  appendMessage: (msg) =>
    set((s) => {
      const existing = s.messages[msg.channel_id] ?? []
      // Deduplicate
      if (existing.some((m) => m.id === msg.id)) return s

      const merged = [...existing, msg].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      )
      const nextMsgs = {
        ...s.messages,
        [msg.channel_id]: merged,
      }

      // Bump last_message_at on the channel and increment unread unless
      // the user is actively viewing this specific channel in the open panel.
      const channels = s.channels.map((c) => {
        if (c.id !== msg.channel_id) return c
        const isActivelyViewing = s.panelOpen && s.activeChannelId === c.id
        return {
          ...c,
          last_message_at: msg.created_at,
          unread_count: isActivelyViewing ? 0 : (c.unread_count ?? 0) + 1,
        }
      })

      return { messages: nextMsgs, channels }
    }),

  prependMessages: (channelId, msgs) =>
    set((s) => {
      const existing = s.messages[channelId] ?? []
      const existingIds = new Set(existing.map((m) => m.id))
      const deduped = msgs.filter((m) => !existingIds.has(m.id))
      const merged = [...deduped, ...existing].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      )
      return {
        messages: {
          ...s.messages,
          [channelId]: merged,
        },
      }
    }),

  setUserOnline: (userId, online) =>
    set((s) => {
      if (online) {
        if (s.onlineUsers.includes(userId)) return s
        return { onlineUsers: [...s.onlineUsers, userId] }
      }
      return { onlineUsers: s.onlineUsers.filter((u) => u !== userId) }
    }),

  setTyping: (channelId, userId, isTyping) =>
    set((s) => {
      const current = s.typingUsers[channelId] ?? []
      let next: string[]
      if (isTyping) {
        next = current.includes(userId) ? current : [...current, userId]
      } else {
        next = current.filter((u) => u !== userId)
      }
      return { typingUsers: { ...s.typingUsers, [channelId]: next } }
    }),

  decrementUnread: (channelId) =>
    set((s) => ({
      channels: s.channels.map((c) =>
        c.id === channelId ? { ...c, unread_count: 0 } : c,
      ),
    })),
}))

/** Derived: total unread across all channels. */
export function useTotalUnread(): number {
  return useTeamStore((s) =>
    s.channels.reduce((sum, c) => sum + (c.unread_count ?? 0), 0),
  )
}
